import type { EMemberSnapshotDataDomains, TLinkedinConnection, TLinkedInMapperData } from '../types/LinkedIn.ts'
import type { UserPrivateProfile, UserProfile } from '../types/User.ts'
import { LINKED_IN_FIELD_DOMAINS, LINKEDIN_DOMAIN_FIELDS } from './../constants/linkedIn.ts'

type TField = typeof LINKEDIN_DOMAIN_FIELDS

/**
 * Checks if synchronization is available for a given field within the specified domains.
 *
 * @param field The field to check for synchronization availability.
 * @param domains The array of domains to check against.
 * @returns `true` if synchronization is available for the field, `false` otherwise.
 */
export const isSynchronizationAvailable = (
    field: TField[keyof TField][number],
    domains: TLinkedinConnection['domains'] = []
): boolean => {
    return !!domains.find((domain) => LINKEDIN_DOMAIN_FIELDS[domain].includes(field))
}

/**
 * Checks if a field is included in the synchronization fields.
 *
 * @param field The field to check.
 * @param fields The array of fields that are currently synchronized.
 * @returns `true` if the field is synchronized, `false` otherwise.
 */
export const isFieldSynchronized = (
    field: TField[keyof TField][number],
    fields: TLinkedinConnection['syncFields'] = []
): boolean => {
    return fields.includes(field)
}

/**
 * Toggles the synchronization state of a field in the `syncFields` array.
 *
 * @param value Determines whether to add or remove the field.
 * @param field The field to be synchronized or desynchronized.
 * @param fields The existing array of synchronized fields.
 * @returns The updated array of synchronized fields.
 */
export const switchFieldSynchronization = (
    value: boolean,
    field: TField[keyof TField][number],
    fields: TLinkedinConnection['syncFields'] = []
): TLinkedinConnection['syncFields'] => {
    const newFields = new Set(fields)

    if (value) newFields.add(field)
    else newFields.delete(field)

    return Array.from(newFields)
}

/**
 * Determines if a field is disabled based on its synchronization status and available domains.
 *
 * @param field The field to check.
 * @param data The data object containing domains and sync fields.
 * @returns `true` if the field is disabled, `false` otherwise.
 */
export const isFieldDisabled = (
    field: TField[keyof TField][number],
    data: Pick<TLinkedinConnection, 'domains' | 'syncFields'> | null
): boolean => {
    const { domains, syncFields } = data || { domains: [], syncFields: [] }
    return isSynchronizationAvailable(field, domains) && isFieldSynchronized(field, syncFields)
}

const isValidField = <T extends object>(obj: T, key: keyof any): key is keyof T => {
    return key in obj
}

/**
 * Switches the value of a field based on synchronization status and profile data.
 *
 * @param value Indicates whether the field is synchronized.
 * @param field The field whose value is being switched.
 * @param old The existing value of the field.
 * @param profile The profile object containing public and private fields.
 * @returns The new field value if synchronized and present in the profile, the old value if not synchronized, or `undefined` if the field is not found.
 */
export const switchFieldValue = (
    value: boolean,
    field: TField[keyof TField][number],
    old: UserProfile[keyof UserProfile] | UserPrivateProfile[keyof UserPrivateProfile],
    profile: TLinkedInMapperData['profile'] | null
) => {
    // If is not synchronized, return old value
    if (!value) return old

    // If is synchronized, but there is no profile, return undefined
    if (!profile) return undefined

    // If is synchronized and there is a profile, return the new value
    if (isValidField(profile.public, field)) {
        return profile.public[field]
    }
    if (isValidField(profile.private, field)) {
        return profile.private[field]
    }

    // If is synchronized, but the field is not present, return undefined
    return undefined
}

/**
 * Previews the value of a field based on synchronization status and profile data.
 *
 * @param value Indicates whether the field is synchronized.
 * @param field The field whose value is being previewed.
 * @param old The existing value of the field.
 * @param profile The profile object containing public and private fields.
 * @returns The old field value if synchronized, the new value from the profile if not synchronized and available, or `undefined` if the field is not found.
 */
export const previewFieldValue = (
    value: boolean,
    field: TField[keyof TField][number],
    old: UserProfile[keyof UserProfile] | UserPrivateProfile[keyof UserPrivateProfile],
    profile: TLinkedInMapperData['profile'] | null
) => {
    // If is synchronized, return old value
    if (value) return old

    // If is not synchronized, but there is no profile, return undefined
    if (!profile) return undefined

    // If is not synchronized and there is a profile, return the new value
    if (isValidField(profile.public, field)) {
        return profile.public[field]
    }
    if (isValidField(profile.private, field)) {
        return profile.private[field]
    }

    // If is not synchronized, but the field is not present, return undefined
    return undefined
}

export const isFieldFetched = (
    field: TField[keyof TField][number],
    domains: EMemberSnapshotDataDomains[] | null,
    fetchedDomains: EMemberSnapshotDataDomains[] | null
) => {
    if (!domains || !fetchedDomains) return false

    const fieldDomains = LINKED_IN_FIELD_DOMAINS[field]

    const isFetched = fieldDomains.some((domain) => domains.includes(domain) && fetchedDomains.includes(domain))

    return isFetched
}

/**
 * Generates props for a field based on its synchronization status.
 *
 * @param field The field to check for synchronization.
 * @param data The data object containing sync fields.
 * @returns Returns an empty object if the field is not synchronized.
 * If the field is synchronized, returns an object with `hasSuffix` set to `true`
 * and `suffixIcon` set to `'linkedin'`.
 *
 * @example
 * ```vue
 * <template>
 *     <CInput v-bind="synchronizedFieldProps('someField', linkedinConnectionData)" />
 * </template>
 * ```
 */
export const synchronizedFieldProps = (
    field: TField[keyof TField][number],
    data?: Pick<TLinkedinConnection, 'syncFields'> | null
) => {
    const isSynchronized = isFieldSynchronized(field, data?.syncFields)

    if (!isSynchronized) return {}

    return {
        hasSuffix: true,
        suffixIcon: 'linkedin'
    }
}
